import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { FinanceBadge, formatMoney, todayInput } from "../financeUtils";

const PAGE_SIZE = 10;
const EMPTY_FORM = {
  name: "",
  department: "",
  projectId: "",
  periodStart: todayInput(),
  periodEnd: "",
  allocatedAmount: "",
  spentAmount: "",
  status: "Active",
};

function usagePct(budget) {
  if (!budget.allocatedAmount) return 0;
  return Math.min(100, Math.round((Number(budget.spentAmount || 0) / Number(budget.allocatedAmount || 1)) * 100));
}

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const res = await apiFetch("/api/projects");
      if (res.ok) setProjects(await res.json());
    } catch {
      setProjects([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/finance/budgets");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setBudgets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load budgets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(budgets.length / PAGE_SIZE));
  const pagedBudgets = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return budgets.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [budgets, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM, periodStart: todayInput() });
    setModalOpen(true);
  }

  async function createBudget(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/finance/budgets", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          projectId: form.projectId || null,
          allocatedAmount: Number(form.allocatedAmount || 0),
          spentAmount: Number(form.spentAmount || 0),
          periodStart: form.periodStart ? `${form.periodStart}T00:00:00` : null,
          periodEnd: form.periodEnd ? `${form.periodEnd}T00:00:00` : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create budget.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="finance-page">
      <PageHeader
        title="Budgets"
        subtitle="Track department and project budgets with used vs remaining amounts."
        actions={<button className="primary" onClick={openCreate}>Create Budget</button>}
      />

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card finance-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>{[1, 2, 3, 4].map((item) => <Loader key={item} type="skeleton" height={58} style={{ marginBottom: 10 }} />)}</div>
        ) : budgets.length === 0 ? (
          <EmptyState title="No budgets yet" subtitle="Create budgets for departments, projects, or operating periods." action={<button className="primary" onClick={openCreate}>Create Budget</button>} />
        ) : (
          <>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Budget</th>
                    <th>Scope</th>
                    <th>Period</th>
                    <th>Allocated</th>
                    <th>Spent</th>
                    <th>Usage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBudgets.map((budget) => {
                    const pct = usagePct(budget);
                    return (
                      <tr key={budget.id}>
                        <td>
                          <div className="finance-row-title">{budget.name}</div>
                          <div className="finance-row-subtitle">{budget.projectName || budget.department || "General"}</div>
                        </td>
                        <td>{budget.projectName || budget.department || "-"}</td>
                        <td>{formatDate(budget.periodStart)} to {formatDate(budget.periodEnd)}</td>
                        <td>{formatMoney(budget.allocatedAmount)}</td>
                        <td>{formatMoney(budget.spentAmount)}</td>
                        <td>
                          <div className="finance-progress compact"><span style={{ width: `${pct}%` }} /></div>
                          <div className="finance-row-subtitle">{pct}% used</div>
                        </td>
                        <td><FinanceBadge status={budget.status} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={budgets.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createBudget} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Budget</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Budget Name
                <input value={form.name} onChange={(event) => updateField("name", event.target.value)} required />
              </label>
              <label>
                Department
                <input value={form.department} onChange={(event) => updateField("department", event.target.value)} />
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  <option>Active</option>
                  <option>On Hold</option>
                  <option>Closed</option>
                </select>
              </label>
              <label>
                Period Start
                <input type="date" value={form.periodStart} onChange={(event) => updateField("periodStart", event.target.value)} />
              </label>
              <label>
                Period End
                <input type="date" value={form.periodEnd} onChange={(event) => updateField("periodEnd", event.target.value)} />
              </label>
              <label>
                Allocated Amount
                <input type="number" min="0" value={form.allocatedAmount} onChange={(event) => updateField("allocatedAmount", event.target.value)} required />
              </label>
              <label>
                Spent Amount
                <input type="number" min="0" value={form.spentAmount} onChange={(event) => updateField("spentAmount", event.target.value)} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Budget"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
